import * as XLSX from 'xlsx';
import { supabase } from '@/lib/supabase';
import type { DateRange, SalesRow, SalesStore } from '@/lib/salesAnalytics';

/** Columns the page reads. Generated columns (family/platform/channel/is_return) come from the DB. */
const SELECT_COLS =
  'entry_no, posting_date, document_no, document_type, item_no, description, units, revenue, cogs, ' +
  'location_code, store_code, serial_no, created_by, channel, is_return, family, platform';

/** BC store code → store. Several codes can map to one store; see 20260928120000_sales_stores.sql. */
export async function fetchSalesStores(): Promise<Map<string, SalesStore>> {
  const { data, error } = await supabase.from('sales_stores').select('code, store, store_group, closed');
  if (error) throw error;
  return new Map(((data ?? []) as SalesStore[]).map(s => [s.code, s]));
}

/** Supabase caps a single response at 1000 rows by default — page through it. */
const PAGE = 1000;

export async function fetchSalesEntries(range: DateRange): Promise<SalesRow[]> {
  const out: SalesRow[] = [];
  for (let offset = 0; ; offset += PAGE) {
    const { data, error } = await supabase
      .from('sales_entries')
      .select(SELECT_COLS)
      .gte('posting_date', range.from)
      .lte('posting_date', range.to)
      .order('posting_date', { ascending: false })
      .order('entry_no', { ascending: false })
      .range(offset, offset + PAGE - 1);

    if (error) throw error;
    const rows = (data ?? []) as unknown as SalesRow[];
    out.push(...rows);
    if (rows.length < PAGE) break;
  }
  return out;
}

/** Oldest and newest posting dates on record — bounds the "All time" preset. */
export async function fetchSalesDateBounds(): Promise<{ earliest: string | null; latest: string | null }> {
  const [oldest, newest] = await Promise.all([
    supabase.from('sales_entries').select('posting_date').order('posting_date', { ascending: true }).limit(1).maybeSingle(),
    supabase.from('sales_entries').select('posting_date').order('posting_date', { ascending: false }).limit(1).maybeSingle(),
  ]);
  return {
    earliest: (oldest.data?.posting_date as string | undefined) ?? null,
    latest: (newest.data?.posting_date as string | undefined) ?? null,
  };
}

// ── Sale → assembly order linking ────────────────────────────────────────────

export interface AssemblyOrderRef {
  id: string;
  order_number: string;
  build_sku: string | null;
  /** Which unit of a multi-unit order carries this serial, 1-based. */
  unit: number;
  quantity: number;
  /** Set when more than one assembly order claims the same serial. */
  ambiguous: boolean;
}

/** One row of assembly_orders_for_sales. */
interface AssemblyOrderMatch {
  serial: string;
  assembly_order_id: string;
  order_number: string;
  build_sku: string | null;
  unit: number;
  quantity: number | null;
}

export interface AssemblyOrderSerialIndex {
  bySerial: Map<string, AssemblyOrderRef>;
  /**
   * Creation date of the earliest assembly order carrying a serial. Sales posted
   * before this predate serial capture entirely (the column only arrived in
   * migration 20260320_ao_serial_number.sql) and can never link — worth telling
   * apart from a serial that genuinely should have matched. Derived rather than
   * hardcoded so it stays correct if history is backfilled.
   */
  trackingFrom: string | null;
}

/**
 * Serials are compared case- and whitespace-insensitively: the same unit is typed
 * into BC and into the Hub by different people. Leading and trailing punctuation
 * goes too - BC exports wrap serials in stray separators ('ABC123 ,') and a
 * trailing comma is not part of anyone's serial number.
 *
 * Must stay in step with public.normalize_serial() in
 * 20260820_normalize_serial_matching.sql: the two sides of a match are compared
 * with one of these each, so a difference between them is a missed link.
 * Internal characters are preserved on purpose - 'ABC , DEF' is two serials in
 * one field, not one serial containing a comma.
 */
export const normaliseSerial = (s: string | null | undefined): string =>
  (s ?? '').trim().toUpperCase()
    .replace(/^[^0-9A-Z]+/, '')
    .replace(/[^0-9A-Z]+$/, '');

/**
 * Storage form of a serial: the same edge trim, but case is left alone because
 * this value is displayed and exported, not just matched on. Punctuation-only
 * serials (a bare ',') become null - they carry no identity.
 */
export const tidySerial = (s: string | null): string | null => {
  if (s == null) return null;
  const out = s.trim().replace(/^[^0-9A-Za-z]+/, '').replace(/[^0-9A-Za-z]+$/, '');
  return out === '' ? null : out;
};

/**
 * Placeholders people type when there is no serial. These must be rejected on
 * both sides before matching, or every "N/A" sale links to whichever "N/A"
 * assembly order happened to be indexed first — a confident wrong answer, which
 * is worse than an honest blank.
 *
 * Mirrored in Postgres as public.is_placeholder_serial (migration
 * 20260824174500). The two ran out of step for a while and it cost a wrong
 * on-hand figure, so change them together.
 */
const PLACEHOLDER_SERIALS = new Set([
  'NA', 'NONE', 'NIL', 'NULL', 'NOSERIAL', 'NOSN', 'SN', 'UNKNOWN', 'PENDING',
  'TBC', 'TBD', 'TBA', 'X', 'XX', 'XXX', 'XXXX', 'ASK', 'MISSING',
]);

export function isPlaceholderSerial(s: string | null | undefined): boolean {
  const bare = normaliseSerial(s).replace(/[^A-Z0-9]/g, '');
  if (!bare) return true;                    // empty, or punctuation only ("-", "--", "?")
  if (/^0+$/.test(bare)) return true;        // "0", "000"
  return PLACEHOLDER_SERIALS.has(bare);
}

/** A serial usable as a matching key, or '' when it is absent or a placeholder. */
export const serialKey = (s: string | null | undefined): string =>
  isPlaceholderSerial(s) ? '' : normaliseSerial(s);

/**
 * Serial number → the assembly order that built it, for the serials appearing in
 * sales over one date range.
 *
 * Serial is the only reliable key. A SKU is shared by every unit of a model, so
 * it can narrow candidates but never identify one — which is why a sale with no
 * serial cannot be linked by inference, only by hand.
 *
 * The join runs in Postgres (assembly_orders_for_sales) rather than here. The
 * earlier version pulled every assembly order into the browser to build this
 * map, which grew with the table however narrow the range was.
 */
export async function fetchAssemblyOrderIndex(range: DateRange): Promise<AssemblyOrderSerialIndex> {
  const [matches, tracking] = await Promise.all([
    supabase.rpc('assembly_orders_for_sales', { p_from: range.from, p_to: range.to }),
    supabase.rpc('assembly_order_serial_tracking_start'),
  ]);

  if (matches.error) throw matches.error;
  if (tracking.error) throw tracking.error;

  const bySerial = new Map<string, AssemblyOrderRef>();
  for (const row of (matches.data ?? []) as AssemblyOrderMatch[]) {
    const key = serialKey(row.serial);
    if (!key) continue;
    const seen = bySerial.get(key);
    if (seen) {
      // The function returns every match, so a serial claimed by two orders
      // arrives as two rows. That's a data-entry error — flag it rather than
      // silently keeping whichever sorted first.
      if (seen.id !== row.assembly_order_id) seen.ambiguous = true;
      continue;
    }
    bySerial.set(key, {
      id: row.assembly_order_id,
      order_number: row.order_number,
      build_sku: row.build_sku,
      unit: row.unit,
      quantity: row.quantity ?? 1,
      ambiguous: false,
    });
  }

  return { bySerial, trackingFrom: (tracking.data as string | null) ?? null };
}

export interface SerialSale {
  serial_no: string;
  posting_date: string;
  document_no: string;
  location_code: string;
  units: number;
  is_return: boolean;
}

/**
 * What happened to specific units, keyed by serial — the reverse of the
 * Assembly Order column on the sales report.
 *
 * Via the sales_for_serials RPC rather than sales_entries directly: the table is
 * admin-only, but PAO detail is reachable by managers and technicians, so a
 * direct query would silently return nothing and read as "never sold". The RPC
 * exposes date, document, location and return status only — never revenue or
 * COGS.
 *
 * Placeholder serials are dropped before the call. An order carrying "N/A" would
 * otherwise pull back every unrelated "N/A" sale and claim the unit sold
 * repeatedly.
 */
export async function fetchSalesForSerials(serials: string[]): Promise<SerialSale[]> {
  const real = serials.filter(s => !isPlaceholderSerial(s));
  if (!real.length) return [];
  const { data, error } = await supabase.rpc('sales_for_serials', { p_serials: real });
  if (error) throw error;
  return (data ?? []) as SerialSale[];
}

export interface UnitsSold {
  units_net: number;
}

/**
 * All-time net units sold for one SKU. Goes through the units_sold_by_sku RPC
 * rather than sales_entries directly: the table is admin-only, but build detail
 * is reachable by every role, so a direct query would silently return 0 for
 * technicians, managers and retail. The RPC exposes counts only — never
 * revenue or COGS.
 */
export async function fetchUnitsSold(itemNo: string): Promise<UnitsSold | null> {
  const { data, error } = await supabase.rpc('units_sold_by_sku', { p_item_no: itemNo });
  if (error) throw error;
  const row = (data ?? [])[0] as UnitsSold | undefined;
  return row ?? null;
}

// ── BC "Item Ledger Entries" import ──────────────────────────────────────────

export interface ParsedSalesEntry {
  entry_no: number;
  posting_date: string;
  entry_type: string;
  document_type: string | null;
  document_no: string;
  item_no: string;
  description: string | null;
  units: number;
  revenue: number;
  cogs: number;
  location_code: string;
  serial_no: string | null;
  created_by: string | null;
  invoiced_quantity: number | null;
  brand_code: string | null;
  store_code: string | null;
  business_unit_code: string | null;
  item_category_code: string | null;
  variant_code: string | null;
  source_file: string;
}

export interface ParseResult {
  entries: ParsedSalesEntry[];
  /** Rows dropped, with the reason — surfaced in the dialog rather than silently ignored. */
  skipped: { row: number; reason: string }[];
  /** Header names present in the file but not mapped — helps diagnose a changed export. */
  unmappedHeaders: string[];
  missingRequired: string[];
}

/** Header matching is whitespace/punctuation-insensitive so BC's spacing can drift. */
function normaliseHeader(h: string): string {
  return String(h ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Aliases per field, in preference order. BC localises and reformats these
 * headers between versions, so match on the normalised form.
 */
const COLUMN_ALIASES: Record<string, string[]> = {
  entry_no: ['entryno'],
  posting_date: ['postingdate'],
  entry_type: ['entrytype'],
  document_type: ['documenttype'],
  document_no: ['documentno'],
  item_no: ['itemno'],
  description: ['description'],
  quantity: ['quantity'],
  invoiced_quantity: ['invoicedquantity'],
  location_code: ['locationcode'],
  cost_amount: ['costamountactual', 'costamount'],
  sales_amount: ['salesamountactual', 'salesamount'],
  serial_no: ['serialno'],
  created_by: ['createdby', 'createduserid', 'usercode'],
  brand_code: ['brandcode'],
  store_code: ['storecode'],
  business_unit_code: ['businessunitcode'],
  item_category_code: ['itemcategorycode'],
  variant_code: ['variantcode'],
};

const REQUIRED_FIELDS = ['entry_no', 'posting_date', 'item_no', 'quantity', 'location_code', 'sales_amount', 'cost_amount'];

/**
 * Excel stores a date as a serial number. Decoding it via `new Date(serial)`
 * lands on a UTC instant that renders as the previous day in any positive-offset
 * timezone, so go through SSF, which yields calendar parts with no timezone in
 * play at all.
 */
function excelDateToISO(value: unknown): string | null {
  if (value instanceof Date) {
    // cellDates already applied by a caller — read back the UTC parts it stored.
    const y = value.getUTCFullYear();
    return `${y}-${String(value.getUTCMonth() + 1).padStart(2, '0')}-${String(value.getUTCDate()).padStart(2, '0')}`;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const parts = XLSX.SSF.parse_date_code(value);
    if (!parts || !parts.y) return null;
    return `${parts.y}-${String(parts.m).padStart(2, '0')}-${String(parts.d).padStart(2, '0')}`;
  }
  const text = String(value ?? '').trim();
  if (!text) return null;
  // Already ISO
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  // BC's "Edit in Excel" often writes M/D/YY or D/M/YYYY depending on locale.
  const slash = text.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{2,4})$/);
  if (slash) {
    const [, a, b, y] = slash;
    let year = Number(y);
    if (year < 100) year += 2000;
    // Ambiguous without the workbook locale; disambiguate only when one is > 12.
    let month = Number(a), day = Number(b);
    if (month > 12) { month = Number(b); day = Number(a); }
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  return null;
}

function num(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const cleaned = String(value ?? '').replace(/[^0-9.,-]/g, '').replace(/,/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function text(value: unknown): string | null {
  const s = String(value ?? '').trim();
  return s === '' ? null : s;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Parse a BC Item Ledger Entries export.
 *
 * Sign normalisation — BC's convention for a Sale entry is:
 *   sale:   Quantity -1, Cost Amount (Actual) -609.96, Sales Amount (Actual) +741.53
 *   return: Quantity +1, Cost Amount (Actual) +1453.94, Sales Amount (Actual) -2152.53
 * Negating quantity and cost (and leaving sales alone) makes a sale positive on
 * all three, a return negative on all three, and `revenue - cogs` the margin in
 * both cases — so totals stay correct over any mix of the two.
 */
export async function parseItemLedgerFile(file: File): Promise<ParseResult> {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: '', raw: true });

  const skipped: ParseResult['skipped'] = [];
  if (rows.length < 2) {
    return { entries: [], skipped: [{ row: 0, reason: 'File is empty' }], unmappedHeaders: [], missingRequired: [] };
  }

  // BC sometimes writes a title/filter block above the header — find the row
  // that actually contains "Entry No.".
  let headerIdx = rows.findIndex(r => r.some(c => normaliseHeader(String(c)) === 'entryno'));
  if (headerIdx === -1) headerIdx = 0;

  const header = rows[headerIdx].map(c => normaliseHeader(String(c)));
  const colIdx: Record<string, number> = {};
  for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
    for (const alias of aliases) {
      const i = header.indexOf(alias);
      if (i !== -1) { colIdx[field] = i; break; }
    }
  }

  const missingRequired = REQUIRED_FIELDS.filter(f => colIdx[f] === undefined);
  if (missingRequired.length > 0) {
    return { entries: [], skipped: [], unmappedHeaders: [], missingRequired };
  }

  const mapped = new Set(Object.values(colIdx));
  const unmappedHeaders = rows[headerIdx]
    .map((c, i) => ({ name: String(c ?? '').trim(), i }))
    .filter(h => h.name !== '' && !mapped.has(h.i))
    .map(h => h.name);

  const get = (row: unknown[], field: string): unknown =>
    colIdx[field] !== undefined ? row[colIdx[field]] : undefined;

  const entries: ParsedSalesEntry[] = [];
  const seen = new Set<number>();

  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i];
    const excelRow = i + 1; // 1-based, matches what the user sees in Excel

    const entryNo = Math.round(num(get(row, 'entry_no')));
    const itemNo = text(get(row, 'item_no'));
    if (!entryNo && !itemNo) continue; // blank spacer row

    if (!entryNo) { skipped.push({ row: excelRow, reason: 'Missing Entry No.' }); continue; }
    if (!itemNo) { skipped.push({ row: excelRow, reason: 'Missing Item No.' }); continue; }

    const postingDate = excelDateToISO(get(row, 'posting_date'));
    if (!postingDate) { skipped.push({ row: excelRow, reason: 'Unreadable Posting Date' }); continue; }

    // Guard against a file exported without the Entry Type filter applied —
    // purchases and adjustments would otherwise land in the sales table.
    const entryType = text(get(row, 'entry_type')) ?? 'Sale';
    if (entryType.toLowerCase() !== 'sale') {
      skipped.push({ row: excelRow, reason: `Entry Type is "${entryType}", not Sale` });
      continue;
    }

    const quantity = num(get(row, 'quantity'));
    if (quantity === 0) { skipped.push({ row: excelRow, reason: 'Quantity is 0' }); continue; }

    if (seen.has(entryNo)) { skipped.push({ row: excelRow, reason: `Duplicate Entry No. ${entryNo} in file` }); continue; }
    seen.add(entryNo);

    entries.push({
      entry_no: entryNo,
      posting_date: postingDate,
      entry_type: entryType,
      document_type: text(get(row, 'document_type')),
      document_no: text(get(row, 'document_no')) ?? '',
      item_no: itemNo,
      description: text(get(row, 'description')),
      units: -quantity,
      revenue: round2(num(get(row, 'sales_amount'))),
      cogs: round2(-num(get(row, 'cost_amount'))),
      location_code: text(get(row, 'location_code')) ?? '',
      // Trimmed on the way in, so the next export's stray commas never land.
      serial_no: tidySerial(text(get(row, 'serial_no')) ?? null),
      created_by: text(get(row, 'created_by')),
      // Negated to match units, so "units > invoiced_quantity" reads as
      // shipped-but-not-yet-invoiced rather than needing a sign flip first.
      invoiced_quantity: colIdx.invoiced_quantity !== undefined ? -Math.round(num(get(row, 'invoiced_quantity'))) : null,
      brand_code: text(get(row, 'brand_code')),
      store_code: text(get(row, 'store_code')),
      business_unit_code: text(get(row, 'business_unit_code')),
      item_category_code: text(get(row, 'item_category_code')),
      variant_code: text(get(row, 'variant_code')),
      source_file: file.name,
    });
  }

  return { entries, skipped, unmappedHeaders, missingRequired: [] };
}

/** Which of these entry numbers the DB already holds — drives the new/updated preview counts. */
export async function fetchExistingEntryNos(entryNos: number[]): Promise<Set<number>> {
  const found = new Set<number>();
  for (let i = 0; i < entryNos.length; i += 500) {
    const chunk = entryNos.slice(i, i + 500);
    const { data, error } = await supabase
      .from('sales_entries')
      .select('entry_no')
      .in('entry_no', chunk);
    if (error) throw error;
    for (const r of data ?? []) found.add(Number(r.entry_no));
  }
  return found;
}

/**
 * Upsert on entry_no. Update-on-conflict (rather than ignore) is deliberate:
 * BC revalues Cost Amount (Actual) after posting, so a later export of the same
 * entry carries a corrected cost that should win.
 */
export async function importSalesEntries(
  entries: ParsedSalesEntry[],
  onProgress?: (done: number, total: number) => void,
): Promise<{ imported: number; failed: { entryNos: number[]; message: string }[] }> {
  const CHUNK = 500;
  let imported = 0;
  const failed: { entryNos: number[]; message: string }[] = [];

  for (let i = 0; i < entries.length; i += CHUNK) {
    const chunk = entries.slice(i, i + CHUNK);
    const { error } = await supabase
      .from('sales_entries')
      .upsert(chunk, { onConflict: 'entry_no' });

    if (error) {
      failed.push({ entryNos: chunk.map(c => c.entry_no), message: error.message });
    } else {
      imported += chunk.length;
    }
    onProgress?.(Math.min(i + CHUNK, entries.length), entries.length);
  }

  return { imported, failed };
}
